import React from 'react';
import { CardWrapper, Separator } from '../styled/TrainerStyled';
import WordsDetection from '../../common/WordsDetection/WordsDetection';
import { NoKeywordsCard } from '../NoKeywordsCard/TrainerNoKeywordsCard';
import { EXPLICIT_INTEGRATION_SOURCE } from '../../../constants/appRuntime';
import { PhraseGroup } from '../types/detection';
import {
    canShowDontSaySection,
    canShowWordDetectionCallButton,
    isWordDetectionEnabled,
    type TrainerUserInterfaceConfig
} from '../viewState';
import type { KeyPhraseIntegrationWords } from '../../../store/types';

interface TrainerWordDetectionSectionProps {
    whitelist: PhraseGroup[];
    blacklist: PhraseGroup[];
    noWhitelist: boolean;
    noBlacklist: boolean;
    localization: { getText: (key: string) => string };
    optInOn: boolean;
    isCallStarted: boolean;
    callHold: boolean;
    userInterfaceConfig: TrainerUserInterfaceConfig;
    wordsDetectionConfig: {
        configLoaded?: boolean;
        defaultWordsRendered?: number | null;
        isCallStarted?: boolean;
        keyPhraseIntegrationWords?: KeyPhraseIntegrationWords | null;
        sendManualCallEvent: (isStart: boolean) => void;
    };
}

function DoSayCard({
    whitelist,
    localization,
    userInterfaceConfig,
    wordsDetectionConfig,
    isEnabled,
    isButtonShown
}: {
    whitelist: PhraseGroup[];
    localization: { getText: (key: string) => string };
    userInterfaceConfig: TrainerUserInterfaceConfig;
    wordsDetectionConfig: TrainerWordDetectionSectionProps['wordsDetectionConfig'];
    isEnabled: boolean;
    isButtonShown: boolean;
}) {
    return (
        <div data-testid="trainer-card-dosay">
            <WordsDetection
                localization={localization}
                configLoaded={wordsDetectionConfig.configLoaded ?? false}
                defaultWordsRendered={wordsDetectionConfig.defaultWordsRendered}
                isCallStarted={wordsDetectionConfig.isCallStarted}
                keyPhraseIntegrationWords={wordsDetectionConfig.keyPhraseIntegrationWords}
                sendManualCallEvent={wordsDetectionConfig.sendManualCallEvent}
                moveSaidDoSayWordsToBottom={userInterfaceConfig.moveSaidDoSayWordsToBottom}
                displayDoSayAlternatives={userInterfaceConfig.displayDoSayAlternatives}
                displayStartStopKeywords={
                    userInterfaceConfig.explicitIntegrationSource === EXPLICIT_INTEGRATION_SOURCE.KEYPHRASE_AUDIO_EVENT
                }
                isEnabled={isEnabled}
                isButtonShown={isButtonShown}
                type="whitelist"
                words={whitelist}
            />
        </div>
    );
}

function DontSayCard({
    blacklist,
    localization,
    userInterfaceConfig,
    wordsDetectionConfig,
    isEnabled
}: {
    blacklist: PhraseGroup[];
    localization: { getText: (key: string) => string };
    userInterfaceConfig: TrainerUserInterfaceConfig;
    wordsDetectionConfig: TrainerWordDetectionSectionProps['wordsDetectionConfig'];
    isEnabled: boolean;
}) {
    return (
        <CardWrapper data-testid="trainer-card-dontsay">
            <WordsDetection
                localization={localization}
                configLoaded={wordsDetectionConfig.configLoaded ?? false}
                defaultWordsRendered={wordsDetectionConfig.defaultWordsRendered}
                isCallStarted={wordsDetectionConfig.isCallStarted}
                keyPhraseIntegrationWords={wordsDetectionConfig.keyPhraseIntegrationWords}
                sendManualCallEvent={wordsDetectionConfig.sendManualCallEvent}
                moveSaidDoSayWordsToBottom={userInterfaceConfig.moveSaidDoSayWordsToBottom}
                isEnabled={isEnabled}
                type="blacklist"
                words={blacklist}
            />
        </CardWrapper>
    );
}

export function TrainerWordDetectionSection({
    whitelist,
    blacklist,
    noWhitelist,
    noBlacklist,
    localization,
    optInOn,
    isCallStarted,
    callHold,
    userInterfaceConfig,
    wordsDetectionConfig
}: TrainerWordDetectionSectionProps) {
    if (noWhitelist && noBlacklist) {
        return <NoKeywordsCard localization={localization} />;
    }

    const { displayCallOptOutControls, explicitIntegrationSource, displayDontSaySection } = userInterfaceConfig;
    const isEnabled = isWordDetectionEnabled({
        isCallStarted,
        callHold,
        displayCallOptOutControls,
        optInOn
    });
    const shouldShowDontSaySection = canShowDontSaySection(displayDontSaySection);

    return (
        <>
            <DoSayCard
                whitelist={whitelist}
                localization={localization}
                userInterfaceConfig={userInterfaceConfig}
                wordsDetectionConfig={wordsDetectionConfig}
                isEnabled={isEnabled}
                isButtonShown={canShowWordDetectionCallButton({ displayCallOptOutControls, explicitIntegrationSource })}
            />
            {shouldShowDontSaySection ? <Separator /> : null}
            {shouldShowDontSaySection ? (
                <DontSayCard
                    blacklist={blacklist}
                    localization={localization}
                    userInterfaceConfig={userInterfaceConfig}
                    wordsDetectionConfig={wordsDetectionConfig}
                    isEnabled={isEnabled}
                />
            ) : null}
        </>
    );
}
